import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Bell, Play, Eye, Mail, MessageSquare, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import CustomerTabsBar from '../../components/CustomerTabsBar';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REMINDER_META = {
  membership_renewal: { label: 'Membership Renewal', help: 'Notify customer before membership renews.' },
  membership_expired: { label: 'Membership Expired', help: 'Notify when an active membership passes its end date.' },
  water_heater_flush: { label: 'Water Heater Flush', help: 'Annual flush reminder, derived from last completed flush appointment.' },
  annual_inspection: { label: 'Annual Inspection', help: 'Yearly home plumbing inspection nudge.' },
  upcoming_maintenance: { label: 'Upcoming Maintenance', help: 'Reminder before a scheduled appointment.' },
  overdue_payment: { label: 'Overdue Payment', help: 'Nudge customer when an invoice is past due.' },
};

const CH = [
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'sms', label: 'SMS', icon: MessageSquare },
  { key: 'in_app', label: 'In-App', icon: Smartphone },
];

const AdminReminders = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reminders/settings`, { withCredentials: true });
      setSettings(data.settings || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    }
    setLoading(false);
  };

  const updateField = (key, field, value) => {
    setSettings((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const toggleChannel = (key, ch) => {
    const cur = settings[key]?.channels || [];
    const next = cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch];
    updateField(key, 'channels', next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/reminders/settings`, { settings }, { withCredentials: true });
      setSettings(data.settings || {});
      toast.success('Reminder settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const runNow = async () => {
    if (!window.confirm('Run reminders now? This will send notifications based on current settings.')) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/reminders/run-now`, {}, { withCredentials: true });
      toast.success(`Scanned ${data.scanned} — Queued: in-app ${data.queued.in_app}, email ${data.queued.email}, sms ${data.queued.sms} (skipped duplicates ${data.queued.skipped_deduped})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    }
    setRunning(false);
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await axios.get(`${API}/admin/reminders/preview`, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    }
    setPreviewing(false);
  };

  return (
    <div data-testid="admin-reminders-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Bell className="w-6 h-6 text-[#D4AF37]" /> Automated Reminders</h2>
          <p className="text-[#A3A3A3]">Configure timing and channels for every customer reminder. Disabled until you turn them on.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="preview-reminders" onClick={doPreview} disabled={previewing} variant="outline" className="border-[#262626] text-white hover:bg-[#262626]"><Eye className="w-4 h-4 mr-2" /> {previewing ? 'Scanning…' : 'Preview'}</Button>
          <Button data-testid="run-reminders" onClick={runNow} disabled={running} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Play className="w-4 h-4 mr-2" /> {running ? 'Sending…' : 'Run Now'}</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A3A3A3]">Loading…</div>
      ) : (
        <div className="grid gap-4">
          {Object.entries(REMINDER_META).map(([key, meta]) => {
            const cfg = settings[key] || { enabled: false, days_before: 0, channels: [] };
            return (
              <div key={key} data-testid={`reminder-card-${key}`} className="bg-[#141414] border border-[#262626] rounded-md p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-white font-medium">{meta.label}</p>
                    <p className="text-[#A3A3A3] text-sm">{meta.help}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[#A3A3A3] text-xs">Enabled</span>
                    <Switch
                      data-testid={`reminder-toggle-${key}`}
                      checked={!!cfg.enabled}
                      onCheckedChange={(v) => updateField(key, 'enabled', v)}
                    />
                  </div>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-white">Days Before Due</Label>
                    <Input
                      data-testid={`reminder-days-${key}`}
                      type="number"
                      min={0}
                      max={365}
                      value={cfg.days_before ?? 0}
                      onChange={(e) => updateField(key, 'days_before', parseInt(e.target.value || '0', 10))}
                      disabled={!cfg.enabled}
                      className="bg-[#0A0A0A] border-[#262626] text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Channels</Label>
                    <div className="flex gap-2 mt-2">
                      {CH.map((c) => {
                        const Icon = c.icon;
                        const on = (cfg.channels || []).includes(c.key);
                        return (
                          <button
                            key={c.key}
                            type="button"
                            disabled={!cfg.enabled}
                            data-testid={`reminder-ch-${key}-${c.key}`}
                            onClick={() => toggleChannel(key, c.key)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm transition-colors ${
                              on
                                ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                                : 'border-[#262626] text-[#A3A3A3] hover:text-white'
                            } ${!cfg.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            <Icon className="w-4 h-4" />
                            {c.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button data-testid="save-reminders" onClick={save} disabled={saving || loading} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
        {saving ? 'Saving…' : 'Save Settings'}
      </Button>

      {preview && (
        <div data-testid="reminder-preview-list" className="bg-[#141414] border border-[#262626] rounded-md p-5">
          <p className="text-white font-medium mb-2">Preview — {preview.count} reminder(s) would fire now</p>
          {preview.count === 0 ? (
            <p className="text-[#A3A3A3] text-sm">No customers match the configured rules right now.</p>
          ) : (
            <ul className="divide-y divide-[#262626] max-h-96 overflow-y-auto">
              {preview.reminders.map((r, i) => (
                <li key={i} className="py-2 text-sm flex items-center justify-between gap-3">
                  <div>
                    <p className="text-white">{r.title}</p>
                    <p className="text-[#A3A3A3] text-xs">{r.customer_name || r.customer_id} · Due {r.due_date}</p>
                  </div>
                  <span className="text-[#D4AF37] text-xs uppercase">{r.type.replace(/_/g, ' ')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4">
        <p className="text-[#525252] text-xs">
          Email and SMS delivery falls back to dev/preview mode (logs only) until <span className="text-[#A3A3A3]">RESEND_API_KEY</span> and <span className="text-[#A3A3A3]">TWILIO_*</span> are configured. In-app notifications work regardless.
        </p>
      </div>
    </div>
  );
};

export default AdminReminders;
